import * as L from 'leaflet';
import { MAP_FOCUS_COLOR, MAP_FOCUS_DASH_ARRAY } from './map-object-focus';

/** Matches the rendered cluster circle without covering its count or fill. */
export function focusCountBadgeIcon(size: number): L.DivIcon {
  return L.divIcon({
    className: 'map-object-focus-cluster',
    html: `<svg width="${size}" height="${size}" viewBox="0 0 40 40" aria-hidden="true"><circle cx="20" cy="20" r="18" fill="none" stroke="${MAP_FOCUS_COLOR}" stroke-width="2" stroke-dasharray="${MAP_FOCUS_DASH_ARRAY}" stroke-linecap="butt" vector-effect="non-scaling-stroke"/></svg>`,
    iconSize: [size, size], iconAnchor: [size / 2, size / 2],
  });
}

/**
 * The two shapes the map draws when it has to say HOW MANY.
 *
 * One set of sizes, one font, one way of reading a number, wherever a count
 * lands on the map. Before this the map answered the same question two ways -
 * a circle over here, a line of text written across a polygon over there - so
 * nothing on screen said the numbers were the same kind of number, and reading
 * one did not teach you how to read the next.
 *
 * TWO shapes, though, because there are two kinds of count and a reader is
 * entitled to tell them apart at a glance:
 *
 *  - THE CIRCLE stands in for things that are there but not drawn. A cluster
 *    of 24 customers is 24 markers the map folded up, and the circle is a
 *    promise: zoom in and I come apart into pins. It is an object on the map.
 *  - THE CHIP describes a region that is already drawn. The leads and the
 *    customers inside a territory are not hiding under the badge - they are
 *    spread across the patch it sits on, and clicking it will never break it
 *    into anything. It is a label about an area, and it holds more than one
 *    number, which is the other thing a circle cannot do.
 *
 * They were the same shape once, and territories borrowed the cluster circle.
 * What went wrong was not that it looked bad: it looked like a cluster, so a
 * patch's lead count read as a pile of pins waiting to be zoomed into.
 *
 * The colour is the caller's, passed through `--map-feature-color`. Features
 * are already told apart by hue, and a badge that painted every count the same
 * would say the number belongs to something it does not.
 */
export interface CountBadge {
  /** How many. Sizes the circle: a four-digit count needs a wider one. */
  readonly count: number;
  /** What it says. The count, unless the caller has something more to add -
   *  a cluster with part of it selected says `3/12`. */
  readonly label?: string;
  /** Fill. */
  readonly color: string;
  /** Draw the shared selection ring around it. */
  readonly selected?: boolean;
  /** One more class on the icon, for a caller that has to find its own. */
  readonly className?: string;
}

/**
 * How wide the circle is, in pixels, for a count of this size.
 *
 * Grows a step per digit rather than continuously: the number inside is what
 * has to stay readable, and a circle that scaled with the count would make a
 * map of hundreds into a map of blobs.
 */
export function countBadgeSize(count: number): number {
  return 32 + Math.floor(Math.log10(Math.max(1, count))) * 6;
}

/** Type size that keeps the label inside the circle it is written in. */
export function countBadgeFontPx(label: string): number {
  if (label.length <= 2) return 12;
  if (label.length === 3) return 11;
  if (label.length <= 5) return 9;
  if (label.length <= 7) return 8;
  return 7;
}

export function countBadgeIcon(badge: CountBadge): L.DivIcon {
  const label = badge.label ?? String(badge.count);
  const size = countBadgeSize(badge.count);
  const selected = badge.selected ? ' is-selected' : '';
  const extra = badge.className ? ` ${badge.className}` : '';

  return L.divIcon({
    className: `map-feature-cluster-icon${selected}${extra}`,
    html: [
      `<svg class="map-feature-cluster-symbol" viewBox="0 0 40 40" style="--map-feature-color:${escapeAttribute(badge.color)}" aria-hidden="true">`,
      '<circle class="map-feature-cluster-symbol__selection-outline" cx="20" cy="20" r="19"/>',
      '<circle class="map-feature-cluster-symbol__body" cx="20" cy="20" r="18"/>',
      `<text class="map-feature-cluster-symbol__count" x="20" y="20" style="font-size:${countBadgeFontPx(label)}px">${escapeText(label)}</text>`,
      '</svg>',
    ].join(''),
    iconSize: [size, size],
  });
}


/**
 * One number in a chip: how many, and the colour of whatever it counts.
 *
 * WHAT it counts is not in here, because the chip does not draw it. Two
 * numbers and no words is only readable if something else says which is which,
 * and that is the caller's tooltip - which reaches a screen reader, where a
 * word painted inside an SVG marker does not.
 */
export interface CountChipPart {
  readonly count: number;
  /** The counted feature's own colour - lead orange, customer blue - so the
   *  map's existing vocabulary says which number is which, with no legend. */
  readonly color: string;
}

/** A count chip: some numbers about one region, in that region's colour. */
export interface CountChip {
  /** In reading order, and laid along the slash: the first takes the upper
   *  half, the last the lower. Two is what the shape is for - a third would
   *  land on the line rather than either side of it. */
  readonly parts: readonly CountChipPart[];
  /** The region's own colour, drawn as the chip's edge. It is what says which
   *  patch the numbers belong to when two chips sit either side of a border -
   *  the same job the fill does on the circle. */
  readonly color: string;
  readonly className?: string;
}

/** Chip height. Fixed: the numbers inside it are what has to stay readable,
 *  and every chip on a map being one height is what makes them a row of
 *  labels rather than a scattering of blobs. */
const CHIP_HEIGHT = 26;

/** How far into its own half each number sits, as a fraction of each radius.
 *  Off-centre along the same diagonal the slash runs, or both numbers crowd
 *  the line and neither looks like it belongs to a side. */
const NUDGE = 0.34;

/** Empty space at each end of a number. */
const CHIP_PAD = 7;

/** How wide a digit is at a given size, near enough to lay out by. The font is
 *  the system stack, so this is a measurement no stylesheet can promise; the
 *  padding either side absorbs the error. */
const CHIP_DIGIT_RATIO = 0.62;

/** Where the slash meets the outline, as a fraction of each radius: the point
 *  on the ellipse at roughly 45 degrees, so the cut reaches the edge instead
 *  of stopping short of it and leaving two halves joined at the corners. */
const REACH = 0.707;


function chipFontPx(chip: CountChip): number {
  const longest = chip.parts.reduce(
    (widest, part) => Math.max(widest, String(part.count).length), 1);
  // One size for the whole chip, chosen by its longest number. Sizing each
  // cell to its own contents would set 8 and 1000 in different type and make
  // the small number look like a footnote to the big one.
  return countBadgeFontPx('0'.repeat(longest));
}

function chipCellWidth(count: number, fontPx: number): number {
  return CHIP_PAD + String(count).length * fontPx * CHIP_DIGIT_RATIO + CHIP_PAD;
}

/**
 * How much room the chip needs, in pixels.
 *
 * Both numbers, because unlike a circle a chip is not as wide as it is tall,
 * and the two are asked different questions: the width decides whether the
 * chip fits inside its patch at all, and the height is what a name placed
 * above or below it has to clear.
 */
export function countChipSize(chip: CountChip): { width: number; height: number } {
  const fontPx = chipFontPx(chip);
  // THE WIDER NUMBER, not both of them added up. Side by side in a rectangle
  // the badge had to be as wide as its two numbers together; cut diagonally
  // they sit on different rows and each only needs room for itself. Adding
  // them was the rectangle's arithmetic left in place after the shape stopped
  // being one, and it made every badge half as wide again as it had to be -
  // which is that much more of the map covered, and that much more chance of
  // landing on something worth reading.
  const widest = chip.parts.reduce(
    (most, part) => Math.max(most, chipCellWidth(part.count, fontPx)), 0);
  // Room for that number at the height it actually sits: the ellipse has
  // narrowed to sqrt(1 - NUDGE^2) of its width by then, and the number's own
  // centre is already NUDGE of the way out.
  const reachAtRow = Math.sqrt(1 - NUDGE * NUDGE) - NUDGE;
  return { width: Math.round(widest / reachAtRow), height: CHIP_HEIGHT };
}

/**
 * The half of the ellipse on one side of the slash, as a path.
 *
 * Two arcs rather than a clipped shape: a clipPath needs an id, and every
 * territory on screen draws one of these, so ids would have to be unique
 * across markers Leaflet builds independently. An arc needs nothing.
 *
 * The chord runs lower-left to upper-right through the centre, so both arcs
 * are exactly half and the sweep flag is the only thing telling them apart:
 * 1 goes round the top, 0 round the bottom.
 */
function chipHalf(cx: number, cy: number, rx: number, ry: number, upper: boolean): string {
  const x1 = round(cx - rx * REACH);
  const y1 = round(cy + ry * REACH);
  const x2 = round(cx + rx * REACH);
  const y2 = round(cy - ry * REACH);
  return `M ${x1} ${y1} A ${round(rx)} ${round(ry)} 0 0 ${upper ? 1 : 0} ${x2} ${y2} Z`;
}

export function countChipIcon(chip: CountChip): L.DivIcon {
  const fontPx = chipFontPx(chip);
  const { width, height } = countChipSize(chip);
  const cx = width / 2;
  const cy = height / 2;
  const rx = cx - 1;
  const ry = cy - 1;
  const single = chip.parts.length < 2;
  const parts: string[] = [];

  chip.parts.forEach((part, index) => {
    // The first number takes the upper half, the last the lower. A lone number
    // has no halves to be in, so it fills the whole ellipse and sits centred.
    const upper = index === 0;
    const x = single ? cx : cx + (upper ? -1 : 1) * rx * NUDGE;
    const y = single ? cy : cy + (upper ? -1 : 1) * ry * NUDGE;

    parts.push(single
      ? `<ellipse class="map-count-chip__half" cx="${round(cx)}" cy="${round(cy)}" rx="${round(rx)}" ry="${round(ry)}" style="fill:${escapeAttribute(part.color)}"/>`
      : `<path class="map-count-chip__half" d="${chipHalf(cx, cy, rx, ry, upper)}" style="fill:${escapeAttribute(part.color)}"/>`);
    parts.push(
      `<text class="map-count-chip__count" x="${round(x)}" y="${round(y)}" style="font-size:${fontPx}px">${escapeText(String(part.count))}</text>`);
  });

  return L.divIcon({
    className: `map-count-chip-icon${chip.className ? ` ${chip.className}` : ''}`,
    html: [
      `<svg class="map-count-chip" viewBox="0 0 ${width} ${height}" style="--map-feature-color:${escapeAttribute(chip.color)}" aria-hidden="true">`,
      ...parts,
      // The seam between the two fills, drawn over them: two saturated colours
      // meeting edge to edge vibrate along the join, and a hairline of the
      // ground settles it.
      single ? ''
        : `<line class="map-count-chip__split" x1="${round(cx - rx * REACH)}" y1="${round(cy + ry * REACH)}" x2="${round(cx + rx * REACH)}" y2="${round(cy - ry * REACH)}"/>`,
      // The outline last, so the region's own colour rings the whole badge
      // rather than being painted over by either half. Inset by the stroke's
      // own width, or half the edge is drawn outside the box Leaflet gave us
      // and the chip looks clipped at every zoom.
      `<ellipse class="map-count-chip__body" cx="${round(cx)}" cy="${round(cy)}" rx="${round(rx)}" ry="${round(ry)}"/>`,
      '</svg>',
    ].join(''),
    iconSize: [width, height],
  });
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}
/** A colour reaches here from saved data, so it is not trusted to be a colour. */
function escapeAttribute(value: string): string {
  return value.replace(/[^#\w(),.%\s-]/g, '');
}

function escapeText(value: string): string {
  return value.replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!));
}
